import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewListIcon from '@mui/icons-material/ViewList';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import { useGetProductsQuery, useDeleteProductMutation } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useSelector, useDispatch } from 'react-redux';
import { setFilters } from '../../../store/slices/productsSlice';
import ProductsByCategory from './ProductsByCategory';

function ProductsList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.products.filters);
  const { data, isLoading, error } = useGetProductsQuery(filters);
  const [deleteProduct] = useDeleteProductMutation();
  const [tabValue, setTabValue] = useState(0);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const handleSearch = (e) => {
    dispatch(setFilters({ search: e.target.value }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await deleteProduct(id);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading products</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
       
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          mb: { xs: 2, sm: 0 },
        }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              color: '#2C3E50',
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            Products Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/products/new')}
            className="primary-button"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              borderRadius: 3,
              px: { xs: 2, sm: 3 },
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 3,
            minHeight: 48,
            '&.Mui-selected': {
              color: '#4ECDC4',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#4ECDC4',
            height: 3,
            borderRadius: 2,
          },
        }}
      >
        <Tab icon={<ViewListIcon />} label="List View" iconPosition="start" />
        <Tab icon={<CategoryIcon />} label="Category Tree" iconPosition="start" />
      </Tabs>

      {tabValue === 0 && (
        <Card
          sx={{
            background: 'white',
            border: 'none',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ mb: 3 }}>
              <TextField
                placeholder="Search Something..."
                variant="outlined"
                fullWidth
                value={filters.search || ''}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#7F8C8D' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: { xs: '100%', sm: 600 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 5,
                    bgcolor: '#F8F9FA',
                  },
                }}
              />
            </Box>

            <TableContainer
              sx={{
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#4ECDC4',
                  borderRadius: 4,
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Main Category</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Subcategory</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {`Cost Price (${currency})`}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {`Selling Price (${currency})`}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.products?.length > 0 ? (
                    data.products.map((product) => (
                      <TableRow
                        key={product.id}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: '#E8F5E9',
                            cursor: 'pointer',
                          },
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <TableCell sx={{ color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{product.sku}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{product.name}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {product.category?.parent?.name || (
                            <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {product.category?.name || (
                            <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {product.costPrice}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#4ECDC4', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {product.sellingPrice}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          <Chip
                            label={product.isActive ? 'Active' : 'Inactive'}
                            sx={{
                              bgcolor: product.isActive ? '#4ECDC4' : '#E0E0E0',
                              color: product.isActive ? 'white' : '#7F8C8D',
                              fontWeight: 600,
                              borderRadius: 2,
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/products/${product.id}`)}
                              sx={{
                                color: '#4ECDC4',
                                padding: { xs: 0.5, sm: 1 },
                                '&:hover': {
                                  bgcolor: 'rgba(78, 205, 196, 0.1)',
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(product.id)}
                              sx={{
                                color: '#F44336',
                                padding: { xs: 0.5, sm: 1 },
                                '&:hover': {
                                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ color: '#7F8C8D' }}>No products found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && <ProductsByCategory />}
    </Box>
  );
}

export default ProductsList;
